import * as ImagePicker from "expo-image-picker";
import { useState, useEffect } from "react";
import { StyleSheet, View, TouchableOpacity, Image, ActivityIndicator } from "react-native";
import { Text, Icon } from "react-native-paper";

import { VerificationStatus } from "./VerificationStatus";

import { MOBILE_DEBUG_VERSION } from "@/lib/debug";
import { logRocketRedactProps } from "@/lib/logrocket";
import { submitUpload, confirmUpload } from "@/lib/onboarding-api";
import { colors } from "@/lib/theme";
import { uploadFromUri } from "@/lib/upload-from-uri";
import { isVerificationStale } from "@/lib/verification-state";
import { ONBOARDING_MAX_DIMENSION, resizeImageIfNeeded } from "@/utils/resize-image";

interface CameraCaptureProps {
    rnId: string;
    submissionId?: string;
    status?: string;
    /** When the current attempt was submitted, so a dead one stops looking live. */
    submittedAt?: string | null;
    existingUploadUrl?: string;
    onCaptured?: () => void;
}

type CaptureState = "idle" | "capturing" | "uploading" | "verifying" | "done";

export function CameraCapture({
    rnId,
    submissionId,
    status,
    submittedAt,
    existingUploadUrl,
    onCaptured
}: CameraCaptureProps) {
    //  A selfie that has been "verifying" for days is not verifying. Treating
    //  it as idle is what puts the Retake button back, and the photo is the
    //  only thing that can move her forward. Seven applicants were stranded
    //  here, the oldest since 3 June 2026.
    const verifyingIsLive = !isVerificationStale(submittedAt);
    const [state, setState] = useState<CaptureState>(
        status === "ai_verifying" && verifyingIsLive
            ? "verifying"
            : status === "approved"
              ? "done"
              : status === "submitted" && existingUploadUrl && verifyingIsLive
                ? "verifying"
                : "idle"
    );
    const [imageUri, setImageUri] = useState<string | null>(existingUploadUrl ?? null);
    const [_progress, _setProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);

    // Sync internal state when status prop changes (e.g. polling updates)
    useEffect(() => {
        if (status === "rejected" || status === "verification_failed") {
            setState("idle");
        } else if (status === "approved") {
            setState("done");
        } else if (status === "ai_verifying") {
            setState(verifyingIsLive ? "verifying" : "idle");
        }
    }, [status, verifyingIsLive]);

    // Sync image preview when a new upload URL arrives via polling
    useEffect(() => {
        if (existingUploadUrl) {
            setImageUri(existingUploadUrl);
        }
    }, [existingUploadUrl]);

    async function handleCapture() {
        setState("capturing");
        setError(null);
        try {
            const permission = await ImagePicker.requestCameraPermissionsAsync();
            console.info(`[mobile ${MOBILE_DEBUG_VERSION}] camera capture permission result`, {
                granted: permission.granted
            });
            if (!permission.granted) {
                setError("Camera permission required");
                setState("idle");
                return;
            }

            const result = await ImagePicker.launchCameraAsync({
                mediaTypes: ["images"],
                quality: 0.7,
                allowsEditing: false,
                cameraType: ImagePicker.CameraType.front
            });

            console.info(`[mobile ${MOBILE_DEBUG_VERSION}] camera capture result`, {
                canceled: result.canceled,
                assets: result.canceled ? 0 : result.assets.length
            });

            if (result.canceled || result.assets.length === 0) {
                setState("idle");
                return;
            }

            const asset = result.assets[0];
            setImageUri(asset.uri);
            setState("uploading");

            //  Downscale AND transcode before upload. This path used to send
            //  `asset.uri` untouched, which is wrong twice over: a full-size
            //  phone photo can exceed the reader's 5 MB limit, and an iOS HEIC
            //  asset makes Rekognition face-compare throw
            //  InvalidImageFormatException — the failure the nurse sees as a
            //  bare "we couldn't complete this check, that's a problem on our
            //  side". `resizeImageIfNeeded` always re-encodes to JPEG, which is
            //  exactly why it re-encodes even when no downscale is needed.
            const uploadUri = await resizeImageIfNeeded(asset, ONBOARDING_MAX_DIMENSION);

            // Get presigned URL
            const { upload, uploadUrl } = await submitUpload(rnId, {
                filename: `selfie_${Date.now()}.jpg`,
                contentType: "image/jpeg",
                submissionId
            });

            // Upload to S3
            await uploadFromUri({
                uri: uploadUri,
                uploadUrl,
                contentType: "image/jpeg"
            });

            // Confirm upload (triggers AI verification)
            await confirmUpload(rnId, upload.id);
            setState("verifying");
            onCaptured?.();
        } catch (err: unknown) {
            console.error(`[mobile ${MOBILE_DEBUG_VERSION}] camera capture failed`, err);
            setError(err instanceof Error ? err.message : "Upload failed");
            setState("idle");
        }
    }

    if (state === "verifying" || (state === "done" && status)) {
        return (
            <View style={styles.container}>
                {imageUri && (
                    <Image
                        {...logRocketRedactProps()}
                        source={{ uri: imageUri }}
                        style={styles.preview}
                    />
                )}
                <VerificationStatus status={status ?? "ai_verifying"} />
            </View>
        );
    }

    return (
        <View style={styles.container}>
            {imageUri && (
                <Image
                    {...logRocketRedactProps()}
                    source={{ uri: imageUri }}
                    style={styles.preview}
                />
            )}

            {state === "uploading" ? (
                <View style={styles.uploadingRow}>
                    <ActivityIndicator size="small" color={colors.primary} />
                    <Text style={styles.uploadingText}>Uploading...</Text>
                </View>
            ) : (
                <TouchableOpacity style={styles.captureButton} onPress={handleCapture}>
                    <Icon source="camera" size={20} color="#FFFFFF" />
                    <Text style={styles.captureButtonText}>
                        {imageUri ? "Retake Selfie" : "Take Selfie"}
                    </Text>
                </TouchableOpacity>
            )}

            {error && <Text style={styles.errorText}>{error}</Text>}
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        gap: 12
    },
    preview: {
        width: "100%",
        height: 200,
        borderRadius: 12,
        backgroundColor: colors.backgroundSecondary
    },
    captureButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        backgroundColor: colors.primary,
        borderRadius: 10,
        paddingVertical: 14
    },
    captureButtonText: {
        fontSize: 14,
        fontWeight: "700",
        color: "#FFFFFF"
    },
    uploadingRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        paddingVertical: 12
    },
    uploadingText: {
        fontSize: 13,
        color: colors.textSecondary
    },
    errorText: {
        fontSize: 12,
        color: colors.error,
        textAlign: "center"
    }
});
